import { createManualProduct, isManualProduct } from '@/utils/productHelpers'
import { getAccessCondition, getCartMinimumAmountEuros } from '@/components/rewards/utils/rewardAccessConditions'
import { getRewardTypeIcon } from '@/components/rewards/utils/rewardTypeDisplay'

import type { RewardItemSavePayload } from '@/components/rewards/utils/offerRewardPayload'
import type { RewardFormData, RewardStatus, RewardVoucher, UsageTag } from '@/types/reward'
import type { LoyaltyReward, PartnerProduct, RewardUsage } from '@/types/types'
import dayjs from 'dayjs'
import { slugBelongsTo } from './rewardTypeRegistry'

export { getRewardTypeIcon, getRewardTypeLabel } from './rewardTypeDisplay'

export type RewardSummaryItemKind =
  | 'main'
  | 'minOrder'
  | 'validity'
  | 'access'
  | 'scope'
  | 'stores'
  | 'usage'
  | 'audience'

export interface RewardSummaryItem {
  kind?: RewardSummaryItemKind
  text: string
  icon: string
}

const STATUS_COLORS: Record<string, { color: string; bgColor: string }> = {
  'limit-reached': { color: 'text-red-600', bgColor: 'bg-red-50' },
  'expiring-soon': { color: 'text-orange-600', bgColor: 'bg-orange-50' },
  active: { color: 'text-green-600', bgColor: 'bg-green-50' }
}

const REWARD_VOUCHERS: RewardVoucher[] = [
  {
    id: 1,
    slug: 'free_product',
    text: 'Free Product',
    states: ['name', 'points']
  },
  {
    id: 2,
    slug: 'voucher_discount_percent',
    text: 'Discount Percent',
    states: ['name', 'points', 'discountValuePercent', 'validityInDays', 'minimumAmount']
  },
  {
    id: 3,
    slug: 'voucher_discount_amount',
    text: 'Euro Discount',
    states: ['name', 'points', 'voucherDiscountAmount', 'validityInDays', 'minimumAmount']
  },
  {
    id: 4,
    slug: 'voucher_x_for_y',
    text: 'X for Y',
    states: ['name', 'points', 'items']
  },
  {
    id: 5,
    slug: 'voucher_free_delivery',
    text: 'Free Delivery',
    states: ['name', 'points', 'validityInDays', 'minimumOrderAmount']
  }
]

export const getRewardStatus = (reward: LoyaltyReward, t: (key: string) => string): RewardStatus => {
  if (reward.hasReachedClaimLimit) {
    return {
      status: 'limit-reached',
      label: t('pages.reward.library.status.limitReached'),
      ...STATUS_COLORS['limit-reached']
    }
  }

  if (reward.validityInDays && reward.validityInDays <= 7) {
    return {
      status: 'expiring-soon',
      label: t('pages.reward.library.status.expiringSoon'),
      ...STATUS_COLORS['expiring-soon']
    }
  }

  return {
    status: 'active',
    label: t('pages.reward.library.status.active'),
    ...STATUS_COLORS['active']
  }
}

export const formatValidityDays = (days: number | null): string | null => {
  if (!days) return null
  return `${days} jour${days > 1 ? 's' : ''}`
}

export const ensureRewardUsage = (reward: LoyaltyReward, usage?: RewardUsage): void => {
  if (!usage || reward.usages?.includes(usage)) return
  reward.usages = [...(reward.usages ?? []), usage]
}

export const getRewardUsageTags = (usages: RewardUsage[], t: (key: string) => string): UsageTag[] => {
  const tags: UsageTag[] = []

  if (!usages?.length) return tags

  usages.forEach((usage) => {
    switch (usage as RewardUsage) {
      case 'loyalty':
        tags.push({
          label: t('pages.reward.library.usage.loyalty'),
          value: 'loyalty',
          icon: 'HeroHeartIconOutline',
          color: 'orange'
        })
        break
      case 'offer':
        tags.push({
          label: t('pages.reward.library.usage.offer'),
          value: 'offer',
          icon: 'HeroStarIconOutline',
          color: 'violet'
        })
        break
      case 'marketing_flow':
        tags.push({
          label: t('pages.reward.library.usage.marketing'),
          value: 'marketing_flow',
          icon: 'HeroBoltIconOutline',
          color: 'violet'
        })
        break
      case 'autopilot':
        tags.push({
          label: t('pages.reward.library.usage.autopilot'),
          value: 'autopilot',
          icon: 'HeroCogIconOutline',
          color: 'violet'
        })
        break
      case 'contest':
        tags.push({
          label: t('pages.reward.library.usage.contest'),
          value: 'contest',
          icon: 'HeroStarIconOutline',
          color: 'yellow'
        })
        break
      case 'campaign_sms':
        tags.push({
          label: t('pages.reward.library.usage.campaignSms'),
          value: 'campaign_sms',
          icon: 'HeroChatBubbleLeftIconOutline',
          color: 'yellow'
        })
        break
      case 'campaign_push':
        tags.push({
          label: t('pages.reward.library.usage.campaignPush'),
          value: 'campaign_push',
          icon: 'HeroBellIconOutline',
          color: 'yellow'
        })
        break
      default:
        tags.push({
          label: usage.charAt(0).toUpperCase() + usage.slice(1),
          value: usage,
          icon: 'HeroStarIconOutline',
          color: 'gray'
        })
    }
  })

  return tags
}

export const filterActiveRewards = (rewards: LoyaltyReward[]): LoyaltyReward[] => {
  return rewards.filter((reward) => reward.isActive !== false)
}

export type RewardOtherUsageBlockReason = 'xForY' | 'productScope' | 'accessConditions'

const REWARD_OTHER_USAGE_BLOCK_REASON_I18N: Record<RewardOtherUsageBlockReason, string> = {
  xForY: 'pages.offer.reward.xForYDisabledReason.hasOtherUsageThanOffer',
  productScope: 'pages.offer.reward.productScopeDisabledReason.hasOtherUsageThanOffer',
  accessConditions: 'pages.offer.reward.accessConditionsDisabledReason.hasOtherUsageThanOffer'
}

function hasOfferOnlyAccessCondition(reward: LoyaltyReward): boolean {
  if (slugBelongsTo('freeDelivery', reward.rewardType?.slug)) return false
  if (slugBelongsTo('xForY', reward.rewardType?.slug)) return false

  if (getAccessCondition(reward) !== 'none') return true

  if (reward.cartItemCount != null && reward.cartItemCount > 0) return true

  const conditions = reward.conditions ?? []
  if (conditions.some((condition) => condition.type === 'required_products')) return true
  if (
    conditions.some(
      (condition) => condition.type === 'cart_item_count' && typeof condition.value === 'number' && condition.value > 0
    )
  ) {
    return true
  }

  const requiredProductIds =
    reward.requiredProductExternalIds?.filter((id) => typeof id === 'string' && id.trim().length > 0) ?? []
  return requiredProductIds.length > 0
}

function hasProductScopedDiscount(reward: LoyaltyReward): boolean {
  const slug = reward.rewardType?.slug
  if (slugBelongsTo('productScopedDiscount', slug)) return true
  if (!slugBelongsTo('discount', slug)) return false

  if (reward.applyReductionOn === 'products') return true
  if ((reward.items?.length ?? 0) > 0) return true
  // Legacy voucher discounts store POS SKUs in externalId and remain usable outside offers.
  return collectDistinctTargetSkus({ ...reward, externalId: undefined }).length > 0
}

export function getRewardOtherUsageBlockReason(
  reward: LoyaltyReward | null | undefined
): RewardOtherUsageBlockReason | null {
  if (!reward) return null

  if (slugBelongsTo('xForY', reward.rewardType?.slug)) return 'xForY'
  if (hasProductScopedDiscount(reward)) return 'productScope'
  if (hasOfferOnlyAccessCondition(reward)) return 'accessConditions'

  return null
}

export function isRewardAvailableForOtherUsage(reward: LoyaltyReward | null | undefined): boolean {
  return getRewardOtherUsageBlockReason(reward) === null
}

export function getRewardOtherUsageDisabledMessage(
  reward: LoyaltyReward | null | undefined,
  t: (key: string) => string
): string | null {
  const reason = getRewardOtherUsageBlockReason(reward)
  if (!reason) return null
  return t(REWARD_OTHER_USAGE_BLOCK_REASON_I18N[reason])
}

export const isRewardInputDisplayed = (
  selectedVoucher: number,
  slug: string | null = null,
  vouchers: RewardVoucher[] = REWARD_VOUCHERS
): boolean => {
  const voucher = vouchers.find((voucher) => selectedVoucher === voucher.id)
  return voucher?.states?.length > 0 && voucher.states.includes(slug || '')
}

export const checkRewardTextMessage = (
  messageContent: string,
  noSpecialCharacters: (text: string) => boolean
): boolean => {
  return noSpecialCharacters(messageContent)
}

const OFFER_REWARD_SLUGS = [
  'free_product',
  'voucher_x_for_y',
  'voucher_discount_percent',
  'voucher_discount_amount'
] as const

export const getRewardVouchers = (
  t: (key: string) => string,
  rewardTypeIdsBySlug: Record<string, number>
): RewardVoucher[] => {
  const base = [
    {
      id: rewardTypeIdsBySlug.free_product,
      slug: 'free_product',
      text: t('pages.reward.library.type.freeProduct'),
      states: ['name', 'points'],
      isVoucher: false
    },
    {
      id: rewardTypeIdsBySlug.voucher_discount_percent,
      slug: 'voucher_discount_percent',
      text: t('pages.reward.library.type.discountPercent'),
      states: ['name', 'points', 'discountValuePercent', 'validityInDays', 'minimumAmount'],
      isVoucher: true
    },
    {
      id: rewardTypeIdsBySlug.voucher_discount_amount,
      slug: 'voucher_discount_amount',
      text: t('pages.reward.library.type.discountAmount'),
      states: ['name', 'points', 'voucherDiscountAmount', 'validityInDays', 'minimumAmount'],
      isVoucher: true
    },
    {
      id: rewardTypeIdsBySlug.voucher_x_for_y,
      slug: 'voucher_x_for_y',
      text: t('pages.reward.library.type.xForY'),
      states: ['name', 'points', 'items'],
      isVoucher: false
    },
    {
      id: rewardTypeIdsBySlug.voucher_free_delivery,
      slug: 'voucher_free_delivery',
      text: t('pages.reward.library.type.freeDelivery'),
      states: ['name', 'points', 'validityInDays', 'minimumOrderAmount'],
      isVoucher: true
    }
  ]
  return base.map((v) => ({ ...v, id: v.id ?? 0 })) as RewardVoucher[]
}

export const getOfferRewardVouchers = (
  t: (key: string) => string,
  rewardTypeIdsBySlug: Record<string, number>
): (RewardVoucher & { subtitle?: string; example?: string })[] => {
  const all = getRewardVouchers(t, rewardTypeIdsBySlug)
  const subtitles: Record<string, string> = {
    free_product: t('pages.offer.reward.typeSubtitle.freeProduct'),
    voucher_x_for_y: t('pages.offer.reward.typeSubtitle.xForY'),
    voucher_discount_percent: t('pages.offer.reward.typeSubtitle.discountPercent'),
    voucher_discount_amount: t('pages.offer.reward.typeSubtitle.discountAmount')
  }
  const examples: Record<string, string> = {
    free_product: t('pages.offer.reward.typeDesc.freeProduct'),
    voucher_x_for_y: t('pages.offer.reward.typeDesc.xForY'),
    voucher_discount_percent: t('pages.offer.reward.typeDesc.discountPercent'),
    voucher_discount_amount: t('pages.offer.reward.typeDesc.discountAmount')
  }
  return OFFER_REWARD_SLUGS.map((slug) => {
    const v = all.find((x) => x.slug === slug)
    return v ? { ...v, subtitle: subtitles[slug], example: examples[slug] } : null
  }).filter((x): x is RewardVoucher & { subtitle: string; example: string } => x != null)
}

export const formatRewardFormData = (reward: LoyaltyReward | null, t: (key: string) => string): RewardFormData => {
  if (!reward) {
    return {
      name: t('pages.reward.details.namePlaceholder'),
      value: 10,
      validityInDays: null,
      claimLimitQuantity: null,
      isActive: true,
      pictureUrl: '/images/default-profile-picture.jpg',
      externalId: '',
      notificationText: t('pages.reward.details.notificationTextPlaceholder')
    }
  }

  return {
    ...reward,
    value: reward.value || 10,
    isActive: true,
    pictureUrl: reward.pictureUrl || '/images/default-profile-picture.jpg',
    externalId: reward.externalId || '',
    notificationText: reward.notificationTextOverride || t('pages.reward.details.notificationTextPlaceholder')
  }
}

const REWARD_SLUG_TO_TRANSLATION_KEY: Record<string, string> = {
  voucher_discount_percent: 'percentOff',
  voucher_discount_amount: 'amountOff',
  product_discount_percent: 'percentOff',
  product_discount_amount: 'amountOff',
  free_product: 'freeGift',
  voucher_x_for_y: 'xForY',
  voucher_free_delivery: 'freeDelivery'
}

export const getRewardSummaryDetail = (
  reward: LoyaltyReward,
  t: (key: string, params?: Record<string, string | number>) => string
): RewardSummaryItem[] => {
  if (!reward?.rewardType) return []

  const prefix = 'pages.offer.create.summary.detail'
  const parts: RewardSummaryItem[] = []
  const slug = reward.rewardType.slug
  const translationKey = REWARD_SLUG_TO_TRANSLATION_KEY[slug]
  const typeIcon = getRewardTypeIcon(reward.rewardType)

  if (translationKey) {
    if (slug === 'voucher_x_for_y') {
      const buy = Number(reward.value)
      const free = Number(reward.voucherDiscountAmount)

      if (Number.isFinite(buy) && buy > 0 && Number.isFinite(free) && free > 0) {
        const buyKey = buy > 1 ? 'pages.offer.reward.xForYBuyPlural' : 'pages.offer.reward.xForYBuySingular'
        const freeKey = free > 1 ? 'pages.offer.reward.xForYFreePlural' : 'pages.offer.reward.xForYFreeSingular'

        const buyLabel = t(buyKey, { buy })
        const freeLabel = t(freeKey, { free })

        parts.push({ text: t('pages.offer.reward.xForYSummary', { buyLabel, freeLabel }), icon: typeIcon })
      } else {
        parts.push({ text: t(`${prefix}.${translationKey}`), icon: typeIcon })
      }
    } else {
      const value = getRewardValue(reward, slug)
      parts.push({
        text: value != null ? t(`${prefix}.${translationKey}`, { value }) : t(`${prefix}.${translationKey}`),
        icon: typeIcon
      })
    }
  } else if (reward.name) {
    parts.push({ text: reward.name, icon: typeIcon })
  }

  if (reward.minimumOrderAmount != null && reward.minimumOrderAmount > 0) {
    parts.push({
      text: t(`${prefix}.minOrder`, { amount: reward.minimumOrderAmount }),
      icon: 'IconoirEuro'
    })
  } else {
    const cartMinEuros = getCartMinimumAmountEuros(reward)
    if (cartMinEuros != null && cartMinEuros > 0) {
      parts.push({
        text: t(`${prefix}.minOrder`, { amount: cartMinEuros }),
        icon: 'IconoirEuro'
      })
    }
  }

  if (reward.validityInDays != null && reward.validityInDays > 0) {
    parts.push({ text: t(`${prefix}.validity`, { days: reward.validityInDays }), icon: 'IconoirCalendar' })
  }

  if (reward.accessCondition === 'products_required') {
    parts.push({ text: t(`${prefix}.accessProductsRequired`), icon: 'IconoirGift' })
    const reqCondition = (reward.conditions ?? []).find((c) => c.type === 'required_products')
    const conditionItems = reqCondition?.products?.items ?? []
    if (conditionItems.length > 0) {
      const skusWithQty = conditionItems
        .map((item) => (item.quantity > 1 ? `${item.externalId} (x${item.quantity})` : item.externalId))
        .join(', ')
      parts.push({ text: t(`${prefix}.requiredSkus`, { skus: skusWithQty }), icon: 'IconoirGift' })
    } else {
      const reqIds =
        reward.requiredProductExternalIds?.filter((id) => typeof id === 'string' && id.trim().length > 0) ?? []
      if (reqIds.length > 0) {
        parts.push({ text: t(`${prefix}.requiredSkus`, { skus: reqIds.join(', ') }), icon: 'IconoirGift' })
      }
    }
  }

  const targetSkus = collectDistinctTargetSkus(reward)
  const hasTargetedProducts =
    reward.applyReductionOn === 'products' ||
    (reward.applyReductionOn == null && ((reward.items?.length ?? 0) > 0 || targetSkus.length > 0))
  const isDiscountType = [
    'product_discount_amount',
    'product_discount_percent',
    'voucher_discount_percent',
    'voucher_discount_amount'
  ].includes(reward.rewardType?.slug)

  if (isDiscountType) {
    parts.push({
      text: t(`${prefix}.${hasTargetedProducts ? 'scopeProducts' : 'scopeCart'}`),
      icon: 'IconoirShoppingBag'
    })
  }

  if (targetSkus.length > 0) {
    parts.push({ text: t(`${prefix}.targetSkus`, { skus: targetSkus.join(', ') }), icon: 'IconoirScanBarcode' })
  }

  const storeCount = reward.stores?.length ?? reward.storeIds?.length ?? 0
  if (storeCount > 0) {
    const storesKey = storeCount > 1 ? 'storesLimitedOther' : 'storesLimitedOne'
    parts.push({ text: t(`${prefix}.${storesKey}`, { count: storeCount }), icon: 'IconoirShop' })
  }

  let cartItemCount: number | null = reward.cartItemCount != null ? Number(reward.cartItemCount) : null
  if (cartItemCount == null || !Number.isFinite(cartItemCount) || cartItemCount <= 0) {
    const conditions = reward.conditions
    const cartCondition = conditions?.find(
      (condition) => condition?.type === 'cart_item_count' && typeof condition.value === 'number' && condition.value > 0
    )
    if (cartCondition) {
      cartItemCount = Number(cartCondition.value)
    }
  }

  if (cartItemCount != null && Number.isFinite(cartItemCount) && cartItemCount > 0) {
    parts.push({
      text: t('pages.offer.reward.cartItemCountSummary', {
        count: cartItemCount,
        unit: t('pages.offer.reward.cartItemCountUnit')
      }),
      icon: 'IconoirCart'
    })
  }

  const hasSegmentTargeting = (reward.includedSegmentIds?.length ?? 0) > 0 || (reward.includedSegments?.length ?? 0) > 0
  parts.push({
    text: t(`${prefix}.${hasSegmentTargeting ? 'audienceSegment' : 'audienceAll'}`),
    icon: 'IconoirCommunity'
  })

  return parts
}

function getRewardValue(reward: LoyaltyReward, slug: string): number | string | undefined {
  switch (slug) {
    case 'voucher_discount_percent':
      return reward.value ?? reward.discountValuePercent ?? '- '
    case 'voucher_discount_amount':
      return reward.voucherDiscountAmount ?? reward.value ?? '- '
    case 'product_discount_percent':
      return reward.value ?? reward.discountValuePercent ?? '- '
    case 'product_discount_amount':
      return reward.voucherDiscountAmount ?? reward.value ?? '- '
    default:
      return undefined
  }
}

export const getValidityInDaysDisclaimer = (
  validityInDays: number,
  d: (date: Date) => string,
  t: (key: string, options?: { date: string }) => string
): string | null => {
  if (validityInDays > 365) {
    return t('pages.reward.detail.toHighNumber')
  }

  const date = dayjs()
    .add(Number(validityInDays) - 1, 'day')
    .toDate()

  return Number(validityInDays)
    ? t('pages.loyaltyPage.edit.fields.validityInDaysExplain', {
        date: d(date)
      })
    : null
}

export const isSameProduct = (a: PartnerProduct, b: PartnerProduct): boolean =>
  a.partnerId === b.partnerId && a.externalId.toString() === b.externalId.toString()

type ApiProductLike = {
  external_id?: unknown
  externalId?: unknown
  partner_id?: unknown
  partnerId?: unknown
  name?: unknown
  type?: unknown
  image?: unknown
  id?: unknown
}

function normalizePartnerProductFromApi(item: unknown): PartnerProduct {
  const x = item as ApiProductLike
  const externalId =
    x?.externalId != null && String(x.externalId).trim().length > 0
      ? String(x.externalId).trim()
      : x?.external_id != null && String(x.external_id).trim().length > 0
        ? String(x.external_id).trim()
        : ''
  const partnerIdRaw = x?.partnerId ?? x?.partner_id
  const partnerId =
    partnerIdRaw == null
      ? null
      : typeof partnerIdRaw === 'number' && Number.isFinite(partnerIdRaw)
        ? partnerIdRaw
        : Number(partnerIdRaw) || null

  const isManual =
    x?.type === 'manual' || (externalId.length > 0 && (partnerId == null || partnerId === 0) && x?.type !== 'menu')

  const product: PartnerProduct = isManual
    ? createManualProduct(externalId)
    : {
        externalId,
        name: typeof x?.name === 'string' && x.name.length > 0 ? x.name : externalId,
        type: x?.type === 'menu' ? 'menu' : 'product',
        partnerId: partnerId ?? 0
      }
  if (typeof x?.image === 'string' && x.image.length > 0) {
    product.image = x.image
  }
  if (x?.id != null && (typeof x.id === 'number' || typeof x.id === 'string')) {
    product.id = x.id
  }
  return product
}

export function collectDistinctTargetSkus(reward: LoyaltyReward): string[] {
  const seen = new Set<string>()
  const add = (raw: string | null | undefined) => {
    const s = String(raw ?? '').trim()
    if (s.length > 0) seen.add(s)
  }

  for (const item of reward.items ?? []) {
    if (item?.externalId != null) add(String(item.externalId))
  }

  if (typeof reward.externalId === 'string') {
    for (const part of reward.externalId.split(',')) {
      add(part)
    }
  }

  for (const tgt of reward.targets ?? []) {
    if (tgt?.sku) add(String(tgt.sku))
  }

  const legacy = reward.targetSkus ?? reward.target_skus
  if (Array.isArray(legacy)) {
    for (const sku of legacy) {
      if (typeof sku === 'string') add(sku)
    }
  }

  return [...seen]
}

export function normalizeLoyaltyRewardFromApi(reward: LoyaltyReward | null | undefined): void {
  if (!reward) return

  const ext = reward as LoyaltyReward & Record<string, unknown>

  if (reward.validityInDays == null && ext.validity_in_days != null) {
    const v = Number(ext.validity_in_days)
    reward.validityInDays = Number.isFinite(v) && v > 0 ? Math.trunc(v) : null
  }

  if (reward.claimLimitQuantity == null && ext.claim_limit_quantity != null) {
    const v = Number(ext.claim_limit_quantity)
    reward.claimLimitQuantity = Number.isFinite(v) && v > 0 ? Math.trunc(v) : 0
  }

  if (reward.claimLimitDays == null && ext.claim_limit_days != null) {
    const v = Number(ext.claim_limit_days)
    if (Number.isFinite(v) && v > 0) {
      reward.claimLimitDays = Math.trunc(v)
    }
  }

  if (reward.applyReductionOn == null && ext.apply_reduction_on === 'products') {
    reward.applyReductionOn = 'products'
  } else if (reward.applyReductionOn == null && ext.apply_reduction_on === 'cart') {
    reward.applyReductionOn = 'cart'
  }

  const r = reward as LoyaltyReward & { external_id?: unknown }
  if (
    (reward.externalId == null || String(reward.externalId).trim() === '') &&
    r.external_id != null &&
    String(r.external_id).trim() !== ''
  ) {
    reward.externalId = String(r.external_id).trim()
  }

  if (!Array.isArray(reward.items)) {
    reward.items = []
  } else {
    reward.items = reward.items.map((item) => normalizePartnerProductFromApi(item))
  }

  reward.items = reward.items.map((item) => (isManualProduct(item) ? createManualProduct(item.externalId) : item))
}

export function isMerchantRewardProductSkuSlug(slug: string | undefined): boolean {
  return slugBelongsTo('merchantProductSku', slug)
}

export function parseRewardExternalIdCsv(csv: string | null | undefined): string[] {
  const seen = new Set<string>()
  const ordered: string[] = []
  for (const part of String(csv ?? '').split(',')) {
    const sku = part.trim()
    if (!sku || seen.has(sku)) continue
    seen.add(sku)
    ordered.push(sku)
  }

  return ordered
}

export function formatRewardExternalIdCsv(skus: string[] | null | undefined): string {
  const seen = new Set<string>()
  const ordered: string[] = []
  for (const raw of skus ?? []) {
    const sku = String(raw ?? '').trim()
    if (!sku || seen.has(sku)) continue
    seen.add(sku)
    ordered.push(sku)
  }
  return ordered.join(',')
}

export function isProductDiscountExternalIdsValid(reward: LoyaltyReward | null | undefined): boolean {
  const slug = reward?.rewardType?.slug
  if (!slugBelongsTo('productScopedDiscount', slug)) return true
  if ((reward?.items?.length ?? 0) > 0) return true
  return parseRewardExternalIdCsv(reward?.externalId).length > 0
}

export function buildRewardItemsSavePayload(reward: LoyaltyReward): RewardItemSavePayload[] {
  const fromItems = (reward.items ?? [])
    .filter((item) => String(item?.externalId ?? '').trim().length > 0)
    .map((item) => {
      if (isManualProduct(item)) {
        return {
          externalId: item.externalId,
          partnerId: null,
          name: item.name || item.externalId,
          type: 'manual' as const
        }
      }

      const row: RewardItemSavePayload = {
        externalId: item.externalId,
        partnerId: Number(item.partnerId),
        name: item.name,
        type: item.type === 'menu' ? 'menu' : 'product'
      }
      if (item.image) row.image = item.image
      if (item.id != null) row.id = item.id
      return row
    })

  const existingSkus = new Set(fromItems.map((item) => item.externalId.trim()))
  const fromExternalId = parseRewardExternalIdCsv(reward.externalId)
    .filter((sku) => !existingSkus.has(sku))
    .map((sku) => ({
      externalId: sku,
      partnerId: null,
      name: sku,
      type: 'manual' as const
    }))

  return [...fromItems, ...fromExternalId]
}

export function normalizeMerchantRewardSavePayload(
  payload: Record<string, unknown>,
  reward: LoyaltyReward,
  omitItems?: boolean
): void {
  delete payload.external_id
  delete payload.externalId

  const normalizedItems = buildRewardItemsSavePayload(reward)

  if (omitItems) {
    delete payload.items
  } else {
    payload.items = normalizedItems
  }
}
